/* ═══════════════════════════════════════════════════════════════════════════
   LEAVE — holiday, sickness, and a sick child

   The arithmetic of somebody's entitlement. Every figure it works from comes
   out of `law/`, and there is not one statutory number in this file: change
   the country and the same functions answer differently, which is the whole
   point of the split.

   ── Two mechanisms, and they are not the same mechanism ───────────────────

   This is the distinction the module is built around, and getting it wrong
   would hand a business a power the law does not give it.

     EGENMELDING and OMSORGSDAGER are NOTIFICATIONS. The employee states
     them. There is no approval step, and there must not be one on any
     screen: an employer who could decline an egenmelding would be deciding
     whether somebody is ill. What the employer may do is record it, count
     it, and — where the statutory ceiling is passed — say that the next day
     needs a sykmelding instead. That is a fact about the law, not a verdict
     on the person.

     HOLIDAY is a DECISION, and it is the employer's (ferieloven § 6). They
     must discuss it first and give notice, and the employee may demand a
     continuous stretch inside the summer window (§ 7). So holiday genuinely
     has two sides — an ask and an answer — and it is the only one of the
     three that does.

   So `report()` and `ask()` are different verbs here on purpose, and nothing
   in this file lets a business refuse a notification.

   ── Virkedager ───────────────────────────────────────────────────────────

   Holiday is counted in virkedager: every day but Sunday and a statutory
   holiday. A WEEK IS SIX OF THEM. This is the single easiest thing to get
   wrong in Norwegian holiday arithmetic, and getting it wrong understates
   somebody's holiday by a sixth, so the conversion happens here and nowhere
   else. The statutory holidays are computed rather than listed, because
   Easter moves and a hard-coded table is wrong from the year it was written.

   ── What this module refuses to do ────────────────────────────────────────

   Feriepenger. The rate is knowable and the base is somebody's salary, which
   this platform does not hold and should not start holding. Days are a rota
   fact; kroner are payroll's.

   It also does not decide whether somebody is ill, whether a child is
   chronically ill, or whether a workplace has an IA arrangement. Those are
   answers it is given, not answers it works out — and each of them is
   recorded with who said so.
   ═══════════════════════════════════════════════════════════════════════ */
import { rules as rulesFor, HOME } from '../law/index.js';

const DAY = 86400000;
const pad = n => String(n).padStart(2, '0');

/** An ISO date from a Date, in local time — never toISOString, which is UTC
    and turns a Norwegian midnight into the day before. */
export const iso = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Midday, so an hour of daylight saving never moves the date. */
export const at = s => new Date(String(s).slice(0, 10) + 'T12:00:00');

export const yearOf = s => Number(String(s).slice(0, 4));

/** Whole days from one date to another, negative if it is in the past. */
export const daysBetween = (fromISO, toISO) =>
  Math.round((at(toISO) - at(fromISO)) / DAY);

/** Every date from one to the other, both ends included. */
export function range(fromISO, toISO) {
  const out = [];
  if (!fromISO || !toISO || fromISO > toISO) return out;
  for (let d = at(fromISO); iso(d) <= toISO; d.setDate(d.getDate() + 1)) out.push(iso(d));
  return out;
}

/* ── The statutory holidays ───────────────────────────────────────────────
   Computed, not listed. Easter is the Sunday after the first full moon on or
   after 21 March, and every movable Norwegian holiday hangs off it: Maundy
   Thursday and Good Friday before, Easter Monday after, Ascension at +39 and
   Whit Monday at +50. A table typed out for one year is silently wrong for
   the next, and "silently wrong" is the failure mode that costs somebody a
   day of holiday they were entitled to.

   Anonymous Gregorian computus — the arithmetic is standard and the variable
   names are the ones the algorithm is published with. */
export function easter(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${pad(month)}-${pad(day)}`;
}

const plus = (dateISO, n) => {
  const d = at(dateISO);
  d.setDate(d.getDate() + n);
  return iso(d);
};

/**
 * Norway's lovbestemte helge- og høytidsdager for a year.
 *
 * The fixed ones plus the five that move with Easter. Sundays are not in
 * here: they are excluded separately, because a holiday that falls on a
 * Sunday must not be subtracted twice.
 */
export function publicHolidays(year) {
  const e = easter(year);
  return [
    `${year}-01-01`,   // første nyttårsdag
    plus(e, -3),       // skjærtorsdag
    plus(e, -2),       // langfredag
    e,                 // første påskedag
    plus(e, 1),        // andre påskedag
    `${year}-05-01`,   // offentlig høytidsdag
    `${year}-05-17`,   // grunnlovsdag
    plus(e, 39),       // Kristi himmelfartsdag
    plus(e, 49),       // første pinsedag
    plus(e, 50),       // andre pinsedag
    `${year}-12-25`,   // første juledag
    `${year}-12-26`,   // andre juledag
  ].sort();
}

const holidaySet = years => {
  const s = new Set();
  years.forEach(y => publicHolidays(y).forEach(d => s.add(d)));
  return s;
};

/** Is this date a virkedag — a day that counts against holiday? */
export function isVirkedag(dateISO, holidays) {
  const d = at(dateISO);
  if (d.getDay() === 0) return false;
  const set = holidays || holidaySet([yearOf(dateISO)]);
  return !set.has(dateISO);
}

/** How many virkedager a stretch of dates contains, both ends included. */
export function virkedager(fromISO, toISO) {
  const all = range(fromISO, toISO);
  if (!all.length) return 0;
  const set = holidaySet([...new Set(all.map(yearOf))]);
  return all.filter(d => isVirkedag(d, set)).length;
}

/** Virkedager as weeks, to one decimal — six to the week, not five. */
export const asWeeks = (days, country = HOME) =>
  Math.round((Number(days || 0) / rulesFor(country).holiday.perWeek) * 10) / 10;

/* ── Holiday ────────────────────────────────────────────────────────────── */

/**
 * What somebody is entitled to this year, and where each part comes from.
 *
 * `agreed` is the workplace's own figure — five weeks is extremely common
 * and comes from a tariffavtale or the contract, never from the Act. It may
 * raise the statutory entitlement and it may never lower it: an agreement
 * below the statute is void as to the difference, so the floor is applied
 * here rather than trusted to whoever typed the number in.
 *
 * The extra days for the over-60s attach to the YEAR they turn 60, not to
 * their birthday, so somebody born in December has them from January.
 */
export function holidayDue({ born = null, agreed = null } = {}, year, country = HOME) {
  const h = rulesFor(country).holiday;
  const y = Number(year) || new Date().getFullYear();
  const base = Math.max(h.days, Number(agreed) || 0);
  const senior = born && y - Number(born) >= h.extraFromAge;
  const lines = [
    { days: h.days, why: 'The statutory holiday', cite: h.cites.days },
  ];
  if (base > h.days) {
    lines.push({
      days: base - h.days,
      why: `Agreed on top of the Act — from ${h.agreedWhere}`,
      cite: null,
    });
  }
  if (senior) {
    lines.push({
      days: h.extraDays,
      why: `From the year you turn ${h.extraFromAge}`,
      cite: h.cites.extra,
    });
  }
  return {
    total: base + (senior ? h.extraDays : 0),
    statutory: h.days,
    agreed: base > h.days ? base : null,
    senior: !!senior,
    lines,
    unit: rulesFor(country).words.unit,
  };
}

/** The summer window a continuous stretch may be demanded inside. */
export function mainWindow(year, country = HOME) {
  const h = rulesFor(country).holiday;
  return { from: `${year}-${h.mainFrom}`, to: `${year}-${h.mainTo}`, days: h.mainDays };
}

/** Is this stretch inside the summer window at both ends? */
export const inMainWindow = (fromISO, toISO, country = HOME) => {
  const w = mainWindow(yearOf(fromISO), country);
  return fromISO >= w.from && toISO <= w.to;
};

/**
 * Was enough notice given?
 *
 * Reports rather than blocks. The Act's own two months has an exception for
 * what could not be foreseen, and whether something could be is not a thing
 * a calendar knows — so this says how many days there are and what the Act
 * asks for, and the screen prints both.
 */
export function notice(startISO, toldISO, country = HOME) {
  const h = rulesFor(country).holiday;
  const got = daysBetween(toldISO, startISO);
  return {
    got,
    need: h.noticeDays,
    ok: got >= h.noticeDays,
    cite: h.cites.notice,
    say: got >= h.noticeDays
      ? `${got} days' notice.`
      : `${got} days' notice, where the Act asks for ${h.noticeDays} unless `
        + `something unforeseen makes that impossible.`,
  };
}

/** Holiday booked and taken this year, in virkedager. */
export function holidayUsed(rows = [], year) {
  return rows
    .filter(r => r.kind === 'holiday' && r.state !== 'refused' && yearOf(r.from) === Number(year))
    .reduce((n, r) => n + virkedager(r.from, r.to), 0);
}

/* ── Sickness ───────────────────────────────────────────────────────────── */

/** A self-reported spell: consecutive dates reported as one absence. */
const spellsOf = (rows = []) =>
  rows.filter(r => r.kind === 'self' && r.state !== 'withdrawn');

/**
 * What the employee may still self-report, and why.
 *
 * Two different ceilings, because the extended arrangement counts a
 * different thing. The statute limits OCCASIONS — three days at a time, four
 * times in a rolling twelve months — while an extended arrangement is
 * normally written in DAYS, eight at a time and twenty-four in the year. So
 * both are counted and whichever applies is reported.
 *
 * The twelve months roll backwards from the day asked about, not from 1
 * January: § 8-24 says "i løpet av en periode på 12 måneder", and a calendar
 * year would hand everybody four fresh occasions every New Year's Day.
 *
 * `employedSince` is the qualifying period. Somebody two weeks into a job
 * has no right to egenmelding at all, and a screen that let them report one
 * would be setting them up for an unpaid day.
 */
export function selfAllowed(
  { rows = [], employedSince = null, extended = false } = {},
  onISO = iso(new Date()),
  country = HOME,
) {
  const s = rulesFor(country).sick;
  const shape = extended ? s.extended : s.self;
  const since = onISO ? plus(onISO, -365) : null;

  const mine = spellsOf(rows).filter(r => r.from > since && r.from <= onISO);
  const spells = mine.length;
  const days = mine.reduce((n, r) => n + range(r.from, r.to).length, 0);

  const short = employedSince
    ? monthsBetween(employedSince, onISO) < s.self.qualifyingMonths
    : false;

  if (short) {
    return {
      may: false, spells, days,
      maxDays: shape.daysPerSpell,
      why: `Egenmelding takes ${s.self.qualifyingMonths} months in the job `
        + `first. Until then it is a sykmelding from the first day.`,
      cite: s.cites.qualifying,
      extended,
    };
  }

  const spellsLeft = extended ? null : Math.max(0, s.self.spellsPer12Months - spells);
  const daysLeft = extended ? Math.max(0, s.extended.daysPer12Months - days) : null;
  const may = extended ? daysLeft > 0 : spellsLeft > 0;

  return {
    may,
    spells, days,
    spellsLeft, daysLeft,
    maxDays: shape.daysPerSpell,
    extended,
    cite: extended ? s.cites.extended : s.cites.self,
    why: may
      ? (extended
        ? `${daysLeft} of ${s.extended.daysPer12Months} days left in the last twelve months, `
          + `up to ${shape.daysPerSpell} at a time.`
        : `${spellsLeft} of ${s.self.spellsPer12Months} occasions left in the last twelve `
          + `months, up to ${shape.daysPerSpell} days at a time.`)
      : s.afterSelf,
  };
}

/** Whole months between two dates, for the qualifying period. */
export function monthsBetween(fromISO, toISO) {
  const a = at(fromISO);
  const b = at(toISO);
  let m = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() < a.getDate()) m -= 1;
  return m;
}

/**
 * Trim a self-report to what the law actually allows, and say what was
 * trimmed.
 *
 * Not a refusal — the absence stands whatever this returns. What is being
 * decided is how many of those days egenmelding covers, and from which day
 * a sykmelding is needed instead. A screen that silently accepted eight days
 * under a three-day right would be storing up an argument about pay.
 */
export function trimSelf(fromISO, toISO, state, country = HOME) {
  const all = range(fromISO, toISO);
  const max = state.maxDays;
  const kept = all.slice(0, max);
  const over = all.slice(max);
  return {
    from: kept[0] || fromISO,
    to: kept[kept.length - 1] || fromISO,
    days: kept.length,
    overflow: over,
    say: over.length
      ? `Egenmelding covers ${max} day${max === 1 ? '' : 's'} at a time, so the `
        + `${over.length === 1 ? 'last day' : `last ${over.length} days`} needs a `
        + `sykmelding. ${rulesFor(country).sick.afterSelf}`
      : null,
  };
}

/**
 * The employer period.
 *
 * Sixteen calendar days from the first day of the absence, after which the
 * employer may claim from NAV. Calendar days, not working days — the most
 * common mistake and an expensive one, because it moves the date by nearly a
 * week.
 */
export function employerPeriod(fromISO, country = HOME) {
  const s = rulesFor(country).sick;
  return {
    from: fromISO,
    to: plus(fromISO, s.employerDays - 1),
    days: s.employerDays,
    cite: s.cites.employer,
    what: 'Calendar days, not working days.',
  };
}

/* ── A sick child ───────────────────────────────────────────────────────── */

/**
 * Omsorgsdager for the year.
 *
 * Per parent and per calendar year. Three facts change it, and every one of
 * them is something the employee stated rather than something derived: how
 * many children, whether they are alone with the care, and whether one of
 * the children has an approved decision for chronic illness or disability.
 *
 * Nothing here infers a child's health from anything. A chronic-illness
 * decision is NAV's, it is a document, and this records only that the
 * employee says they have one — the figure is what changes, not the child's
 * medical record, which this platform has no business holding.
 */
export function childDue(
  { children = 0, soleCarer = false, chronic = false } = {},
  country = HOME,
) {
  const c = rulesFor(country).child;
  const n = Number(children) || 0;
  if (!n) {
    return { total: 0, lines: [], why: 'No children are recorded, so there is no entitlement to show.' };
  }
  const base = n >= c.daysFrom ? c.daysMany : c.days;
  const lines = [{
    days: base,
    why: n >= c.daysFrom
      ? `${n} children — ${c.daysFrom} or more`
      : `${n} child${n === 1 ? '' : 'ren'}`,
    cite: c.cites.days,
  }];
  let total = base;
  if (soleCarer) {
    lines.push({ days: base * (c.soleCarerTimes - 1), why: 'Alone with the care', cite: c.cites.days });
    total *= c.soleCarerTimes;
  }
  if (chronic) {
    const extra = c.chronicExtra * (soleCarer ? c.soleCarerTimes : 1);
    lines.push({
      days: extra,
      why: 'A child with an approved decision for chronic illness or disability',
      cite: c.cites.days,
    });
    total += extra;
  }
  return { total, lines, why: c.what };
}

/** Sick-child days used this calendar year, and what is left. */
export function childUsed(rows = [], year, due = 0) {
  const used = rows
    .filter(r => r.kind === 'child' && r.state !== 'withdrawn' && yearOf(r.from) === Number(year))
    .reduce((n, r) => n + range(r.from, r.to).length, 0);
  return { used, left: Math.max(0, Number(due) - used), due: Number(due) };
}

/* ── Statistics ─────────────────────────────────────────────────────────── */

/**
 * Sick days over a period, and the rate.
 *
 * The rate is sick days as a share of days the person was rostered — not of
 * calendar days, which flatters everybody by a third, and not of a notional
 * full-time week, which penalises part-timers. If nothing was rostered the
 * rate is null rather than zero: no denominator is not the same fact as no
 * absence, and "0 %" where there is nothing to divide by is a number
 * somebody will put in a report.
 */
export function sickStats(rows = [], { from, to, rostered = 0 } = {}) {
  const inRange = r => r.from <= to && r.to >= from;
  const pick = kind => rows.filter(r => r.kind === kind && r.state !== 'withdrawn' && inRange(r));
  const count = list => list.reduce(
    (n, r) => n + range(r.from < from ? from : r.from, r.to > to ? to : r.to).length, 0);

  const self = pick('self');
  const certified = pick('sick');
  const child = pick('child');
  const selfDays = count(self);
  const certDays = count(certified);
  const days = selfDays + certDays;

  return {
    days,
    selfDays,
    certDays,
    childDays: count(child),
    spells: self.length + certified.length,
    /* Sick-child days are NOT sickness. Somebody minding an ill three-year-
       old is not off ill, and folding the two together produces a figure
       that reads as an attendance problem where there is none. */
    rate: rostered > 0 ? Math.round((days / rostered) * 1000) / 10 : null,
    rostered,
  };
}

/** The kinds of absence, and the verb each one takes. */
export const KINDS = {
  self: { label: 'Egenmelding', verb: 'report', approved: false },
  child: { label: 'Sick child', verb: 'report', approved: false },
  sick: { label: 'Sykmelding', verb: 'record', approved: false },
  holiday: { label: 'Holiday', verb: 'ask', approved: true },
  leave: { label: 'Other leave', verb: 'ask', approved: true },
};

/** Does this kind of absence need somebody's approval? */
export const needsApproval = kind => !!KINDS[kind]?.approved;

/** Is the shift plan out far enough ahead? Reports, and names the section. */
export function planAhead(lastPlannedISO, onISO = iso(new Date()), country = HOME) {
  const p = rulesFor(country).plan;
  /* Never negative. A rota whose last shift was last Tuesday is nought days
     ahead, not minus six — "runs out in -1 days" is a sentence that tells a
     reader the screen is broken rather than that the rota is. */
  const got = Math.max(0, lastPlannedISO ? daysBetween(onISO, lastPlannedISO) : 0);
  return {
    got,
    need: p.aheadDays,
    ok: got >= p.aheadDays,
    cite: p.cite,
    what: p.what,
  };
}
